import { api } from "./client";

/**
 * Map of regex → language tag, used to guess what language a chunk of
 * user-generated text is in. We don't need an industrial-strength detector
 * — the call sites use this only to decide whether to *show* the
 * "Translate" button. False positives just mean the button is shown when
 * not strictly needed; false negatives mean it's hidden when it shouldn't
 * be. Both are acceptable.
 */
const CYRILLIC = /[А-Яа-яЁё]/;
const HEBREW = /[֐-׿]/;
const LATIN = /[A-Za-z]/;

/**
 * Best-effort dominant-script detection: counts characters in each script
 * and returns whichever has the most matches. Returns null if the text
 * is too short or contains no script characters at all (e.g. digits +
 * punctuation only).
 */
export function detectScriptLang(text: string): "ru" | "he" | "en" | null {
  const t = text.trim();
  if (t.length < 3) return null;
  const ru = (t.match(new RegExp(CYRILLIC, "g")) ?? []).length;
  const he = (t.match(new RegExp(HEBREW, "g")) ?? []).length;
  const en = (t.match(new RegExp(LATIN, "g")) ?? []).length;
  if (ru === 0 && he === 0 && en === 0) return null;
  if (ru >= he && ru >= en) return "ru";
  if (he >= en) return "he";
  return "en";
}

/** True when the text needs translating into the target UI language. */
export function needsTranslation(text: string, uiLang: string): boolean {
  const detected = detectScriptLang(text);
  if (!detected) return false;
  return detected !== uiLang;
}

export async function translateText(text: string, to: string): Promise<string> {
  const res = await api<{ translated: string }>("/api/public/translate", {
    method: "POST",
    body: JSON.stringify({ text, to }),
  });
  return res.translated;
}
